import * as R from 'Router/routes';
import { cloneDeep } from 'lodash';
import { datadogLogs } from '@datadog/browser-logs';
import { ERROR_LOGIN_MESSAGE_QUERY_PARAM } from '../customAnalytics/constants';
import { getProcessEnvUrl } from './getUrl';
import { getShiftedDatetimeHeader } from './shiftedDateTime';
import { LOGIN } from 'Router/routes';
import { ValidationError } from '.';
import axios, { AxiosRequestHeaders } from 'axios';

const UNAUTHORIZED_ROUTES = [
  R.LOGIN,
  R.ONLINE_LOGIN,
  R.TWO_FACTOR_AUTH,
  R.TWO_2FA_PHONE,
  R.TWO_2FA_PHONE_MODIFIED,
  R.TWO_2FA_PHONE_ERROR,
  R.CHANGE_PHONE_TWO_FACTOR_AUTH,
  R.ONE_TIME_TOKEN_LOGIN,
];

function getTokenFromSessionStorage() {
  return sessionStorage.getItem('token');
}

const axiosInstance = axios.create({
  baseURL: getProcessEnvUrl('WEB_API'),
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  } as AxiosRequestHeaders,
});

// This is a request interceptor that will run before every request.
// It will add the Authorization header with the token from sessionStorage.
axiosInstance.interceptors.request.use(function getInternalAxiosRequestConfig(
  config,
) {
  const newConfig = cloneDeep(config);

  if (!config.headers['Content-Type'])
    newConfig.headers['Content-Type'] = 'application/json';

  const token = getTokenFromSessionStorage();
  // No token in sessionStorage
  if (!token) {
    return newConfig;
  }
  if (!config.headers.Authorization)
    newConfig.headers.Authorization = `Basic ${token}`;

  // Add time header'
  const shiftedDateTime = getShiftedDatetimeHeader();
  if ('time' in shiftedDateTime) newConfig.headers.time = shiftedDateTime.time;

  return newConfig;
});

axiosInstance.interceptors.response.use(
  function getInternalAxiosResponseConfig(response) {
    return response;
  },
  function onRejected(error) {
    if (error?.response?.status === 401) {
      // Wrong credentials from login endpoint return 401, returns error as normal error
      if (UNAUTHORIZED_ROUTES.includes(window.location.pathname))
        return Promise.reject(error);

      sessionStorage.removeItem('token');

      const loginSessionExpiredRoute = `${LOGIN}?${ERROR_LOGIN_MESSAGE_QUERY_PARAM}=session_expired`;
      const logoutInProcess = sessionStorage.getItem('logoutInProcess');

      if (!logoutInProcess && window.location.pathname !== LOGIN) {
        window.location.assign(loginSessionExpiredRoute);
      }
    }

    if (error?.response?.status === 400 && error?.response?.data?.errors) {
      const validationError = new ValidationError(error.response.data.errors);
      return Promise.reject(validationError);
    }

    if (error?.response?.status >= 500) {
      datadogLogs.logger.error('[Axios interceptor] webapi error', error);
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
